import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/api';
import { gmDisplayName } from '../utils/displayName';

const PublicDraftBoard = () => {
  const { draftId } = useParams();
  const [loading, setLoading] = useState(true);
  const [fantasyDraft, setFantasyDraft] = useState(null);
  const [draftPicks, setDraftPicks] = useState([]);
  const [teamRosters, setTeamRosters] = useState([]);
  const [currentPick, setCurrentPick] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [view, setView] = useState('picks'); // 'picks', 'rosters', 'grid', 'available'
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [sortBy, setSortBy] = useState('overallRating'); // 'overallRating', 'position', 'name'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [positionFilter, setPositionFilter] = useState(''); // '', 'PG', 'SG', 'SF', 'PF', 'C'

  useEffect(() => {
    fetchDraftData();
    
    // Auto-refresh during active draft
    const interval = setInterval(fetchDraftData, 10000); // Refresh every 10 seconds
    setRefreshInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [draftId]);

  const fetchDraftData = async () => {
    try {
      // Fetch all draft data in parallel
      const [draftResponse, picksResponse, rostersResponse, availableResponse] = await Promise.all([
        api.get(`/fantasy-draft/${draftId}/current-pick`),
        api.get(`/fantasy-draft/${draftId}/picks`),
        api.get(`/fantasy-draft/${draftId}/rosters`),
        api.get(`/fantasy-draft/${draftId}/available-players`)
      ]);

      setFantasyDraft(draftResponse.data.fantasyDraft);
      setCurrentPick(draftResponse.data.currentPick);
      setDraftPicks(picksResponse.data.picks);
      setTeamRosters(rostersResponse.data.teams);
      setAvailablePlayers(availableResponse.data.availablePlayers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching draft data:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'not_started': return 'bg-gray-50 dark:bg-gray-8000';
      case 'in_progress': return 'bg-green-500 animate-pulse';
      case 'paused': return 'bg-yellow-500';
      case 'completed': return 'bg-blue-50 dark:bg-blue-900/200';
      default: return 'bg-gray-50 dark:bg-gray-8000';
    }
  };

  const formatStatus = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const getPicksByRound = () => {
    const rounds = {};
    draftPicks.forEach(pick => {
      if (!rounds[pick.round]) {
        rounds[pick.round] = [];
      }
      rounds[pick.round].push(pick);
    });
    return rounds;
  };

  const getNextUpcoming = () => {
    return draftPicks
      .filter(pick => !pick.playerId)
      .slice(0, 5);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading draft board...</p>
        </div>
      </div>
    );
  }

  if (!fantasyDraft) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">Draft Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400">The requested fantasy draft could not be found.</p>
        </div>
      </div>
    );
  }

  const pickedCount = draftPicks.filter(pick => pick.playerId).length;
  const totalPicks = draftPicks.length;
  const completionPercentage = totalPicks > 0 ? (pickedCount / totalPicks * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-700">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{fantasyDraft.name}</h1>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${getStatusColor(fantasyDraft.status)}`}>
                  {formatStatus(fantasyDraft.status)}
                </span>
                {fantasyDraft.status !== 'not_started' && (
                  <span className="text-gray-600 dark:text-gray-400">
                    Round {fantasyDraft.currentRound} of {fantasyDraft.totalRounds}
                  </span>
                )}
                <span className="text-gray-600 dark:text-gray-400">
                  {pickedCount} / {totalPicks} picks made ({completionPercentage.toFixed(1)}%)
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: {new Date().toLocaleTimeString()}</div>
              {fantasyDraft.status === 'in_progress' && (
                <div className="text-sm text-green-600 dark:text-green-400 font-medium mt-1">
                  🔴 LIVE - Updates every 10 seconds
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white dark:bg-gray-800 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div 
              className="bg-blue-50 dark:bg-blue-900/200 h-2 rounded-full transition-all duration-500 ease-out" 
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Current Pick Banner */}
      {currentPick && fantasyDraft.status === 'in_progress' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-700">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="text-center">
              <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">ON THE CLOCK</div>
              <div className="text-xl font-bold text-blue-800">
                {currentPick.team.city} {currentPick.team.name}
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400">
                Pick #{currentPick.overallPick} (Round {currentPick.round}, Pick {currentPick.pickNumber})
              </div>
              {currentPick.team.owner && (
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  GM: {gmDisplayName(currentPick.team.owner, { preferFirstName: true })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setView('picks')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                view === 'picks'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              📋 Draft Picks
            </button>
            <button
              onClick={() => setView('rosters')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                view === 'rosters'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              👥 Team Rosters
            </button>
            <button
              onClick={() => setView('grid')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                view === 'grid'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              📊 Draft Grid
            </button>
            <button
              onClick={() => setView('available')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                view === 'available'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              🎯 Available Players ({availablePlayers.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {view === 'picks' && <PicksView draftPicks={draftPicks} getNextUpcoming={getNextUpcoming} />}
        {view === 'rosters' && <RostersView teamRosters={teamRosters} />}
        {view === 'grid' && <GridView draftPicks={draftPicks} fantasyDraft={fantasyDraft} getPicksByRound={getPicksByRound} />}
        {view === 'available' && (
          <AvailablePlayersView 
            availablePlayers={availablePlayers} 
            sortBy={sortBy} 
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            positionFilter={positionFilter}
            setPositionFilter={setPositionFilter}
          />
        )}
      </div>

      {/* Footer */}
      <div className="bg-white dark:bg-gray-800 border-t mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
            2K Bundesliga Fantasy Draft • {fantasyDraft.draftOrder === 'snake' ? 'Snake Draft' : 'Standard Draft'} • {fantasyDraft.totalRounds} Rounds
          </div>
        </div>
      </div>
    </div>
  );
};

// Picks View Component
const PicksView = ({ draftPicks, getNextUpcoming }) => {
  const recentPicks = draftPicks.filter(pick => pick.playerId).slice(-10).reverse();
  const upcomingPicks = getNextUpcoming();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Recent Picks */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Picks</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {recentPicks.map((pick, index) => (
              <div key={pick.id} className={`flex items-center space-x-4 p-3 rounded-lg ${
                index === 0 ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-800'
              }`}>
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-medium ${
                    index === 0 ? 'bg-blue-50 dark:bg-blue-900/200' : 'bg-gray-50 dark:bg-gray-8000'
                  }`}>
                    {pick.overallPick}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {pick.player?.firstName} {pick.player?.lastName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {pick.player?.position} • {pick.player?.overallRating} OVR
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {pick.team.city} {pick.team.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        R{pick.round}, P{pick.pickNumber}
                      </p>
                    </div>
                  </div>
                </div>
                {index === 0 && (
                  <div className="flex-shrink-0">
                    <span className="text-blue-500 text-sm font-medium">Latest</span>
                  </div>
                )}
              </div>
            ))}
            {recentPicks.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No picks made yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming Picks */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upcoming Picks</h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {upcomingPicks.map((pick, index) => (
              <div key={pick.id} className={`flex items-center justify-between p-3 rounded-lg ${
                index === 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 dark:bg-gray-800'
              }`}>
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-medium ${
                    index === 0 ? 'bg-green-500' : 'bg-gray-400'
                  }`}>
                    {pick.overallPick}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {pick.team.city} {pick.team.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Round {pick.round}, Pick {pick.pickNumber}
                    </p>
                  </div>
                </div>
                {index === 0 && (
                  <span className="text-green-600 dark:text-green-400 text-sm font-medium">Next</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Rosters View Component
const RostersView = ({ teamRosters }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {teamRosters.map(team => {
        const avgOVR = team.players?.length > 0 
          ? (team.players.reduce((sum, p) => sum + p.overallRating, 0) / team.players.length).toFixed(1)
          : '0.0';
        
        return (
          <div key={team.id} className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full mr-2" style={{backgroundColor: team.primaryColor || '#666'}}></div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {team.city} {team.name}
                  </h3>
                  {team.owner && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      GM: {gmDisplayName(team.owner, { preferFirstName: true })}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {team.players?.length || 0} players
                  </div>
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {avgOVR} AVG
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {team.players?.sort((a, b) => b.overallRating - a.overallRating).map(player => (
                  <div key={player.id} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium">{player.firstName} {player.lastName}</span>
                      <span className="text-gray-500 dark:text-gray-400 ml-1">({player.position})</span>
                    </div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">{player.overallRating}</span>
                  </div>
                )) || (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No players drafted
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Grid View Component
const GridView = ({ draftPicks, fantasyDraft, getPicksByRound }) => {
  const rounds = getPicksByRound();
  const teams = [...new Set(draftPicks.map(pick => pick.team.id))]
    .map(teamId => draftPicks.find(pick => pick.team.id === teamId).team);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Draft Grid</h3>
      </div>
      
      <div className="p-6">
        <div className="min-w-full">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Team
                </th>
                {[...Array(fantasyDraft.totalRounds)].map((_, i) => (
                  <th key={i} className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    R{i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {teams.map(team => (
                <tr key={team.id} className="hover:bg-gray-50 dark:bg-gray-800">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full mr-2" style={{backgroundColor: team.primaryColor || '#666'}}></div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {team.abbreviation}
                      </div>
                    </div>
                  </td>
                  {[...Array(fantasyDraft.totalRounds)].map((_, roundIndex) => {
                    const roundNum = roundIndex + 1;
                    const pick = rounds[roundNum]?.find(p => p.team.id === team.id);
                    
                    return (
                      <td key={roundIndex} className="px-2 py-2 text-center">
                        {pick ? (
                          <div className="text-xs">
                            {pick.playerId ? (
                              <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 rounded px-2 py-1">
                                <div className="font-medium text-green-800 truncate max-w-20" title={`${pick.player.firstName} ${pick.player.lastName}`}>
                                  {pick.player.lastName}
                                </div>
                                <div className="text-green-600 dark:text-green-400">
                                  {pick.player.overallRating}
                                </div>
                              </div>
                            ) : (
                              <div className="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded px-2 py-1">
                                <div className="text-gray-600 dark:text-gray-400">#{pick.overallPick}</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-400">-</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Available Players View Component
const AvailablePlayersView = ({ 
  availablePlayers, 
  sortBy, 
  setSortBy, 
  sortOrder, 
  setSortOrder,
  positionFilter,
  setPositionFilter
}) => {
  // Filter players by position
  const filteredPlayers = availablePlayers.filter(player => {
    if (!positionFilter) return true;
    return player.position.includes(positionFilter);
  });

  // Sort players
  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'overallRating':
        comparison = a.overallRating - b.overallRating;
        break;
      case 'position':
        comparison = a.position.localeCompare(b.position);
        break;
      case 'name':
        comparison = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        break;
      default:
        comparison = a.overallRating - b.overallRating;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder(newSortBy === 'overallRating' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Available Players ({filteredPlayers.length} of {availablePlayers.length})
          </h3>
          
          {/* Filters and Sort Controls */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            {/* Position Filter */}
            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="">All Positions</option>
              <option value="PG">Point Guard (PG)</option>
              <option value="SG">Shooting Guard (SG)</option>
              <option value="SF">Small Forward (SF)</option>
              <option value="PF">Power Forward (PF)</option>
              <option value="C">Center (C)</option>
            </select>

            {/* Sort Controls */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleSortChange('overallRating')}
                className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                  sortBy === 'overallRating'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 text-blue-700 dark:text-blue-400'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800'
                }`}
              >
                OVR {getSortIcon('overallRating')}
              </button>
              <button
                onClick={() => handleSortChange('position')}
                className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                  sortBy === 'position'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 text-blue-700 dark:text-blue-400'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800'
                }`}
              >
                Position {getSortIcon('position')}
              </button>
              <button
                onClick={() => handleSortChange('name')}
                className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                  sortBy === 'name'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 text-blue-700 dark:text-blue-400'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800'
                }`}
              >
                Name {getSortIcon('name')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Player
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  OVR
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Current Team
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {sortedPlayers.map((player) => (
                <tr key={player.id} className="hover:bg-gray-50 dark:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {player.firstName} {player.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800">
                      {player.position}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {player.overallRating}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {player.team ? `${player.team.city} ${player.team.name}` : 'Free Agent'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {sortedPlayers.map((player) => (
            <div key={player.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {player.firstName} {player.lastName}
                </div>
                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {player.overallRating}
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800">
                  {player.position}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {player.team ? `${player.team.city} ${player.team.name}` : 'Free Agent'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* No players message */}
        {sortedPlayers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">No available players found</div>
            <div className="text-gray-400 text-sm">
              {positionFilter ? `Try selecting a different position or clearing the filter.` : 'All players have been drafted!'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicDraftBoard;